import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import {
  ApiMarket,
  ApiMarketPrice,
  ApiCycle,
  ApiOrder,
  ApiOrderSettlement,
  ApiLocker,
  ApiLockerOrder,
  ApiLockerUnlockOrder,
  BetSignatureRequest,
  BetSignatureResponse,
  TransferMessageHashRequest,
  TransferMessageHashResponse,
  ApiTvl,
  ApiTvlHistory,
  ApiPriceHistory,
  ApiTokenInfo,
  StakingRewardPending,
  StakingRewardSnapshot,
  LockerRecord,
  LockerClaim,
  PagedResult,
  MarketQueryParams,
  CycleQueryParams,
  LockerQueryParams,
  LockerOrderQueryParams,
  LockerListQueryParams,
  OrderQueryParams,
  PaginatedQueryParams,
  TableSchemaColumn,
  TableRowsResponse,
  TableRowsParams,
} from './api.types';
import type { CustosApi } from './api.provider';
import {
  MOCK_API_MARKETS,
  MOCK_API_MARKET_PRICES,
  MOCK_API_MARKET_PRICE,
  MOCK_API_CYCLES,
  MOCK_API_ORDERS,
  MOCK_API_ORDER_SETTLEMENTS,
  MOCK_API_LOCKER,
  MOCK_API_LOCKER_ORDERS,
  MOCK_BET_SIGNATURE_RESPONSE,
  MOCK_TRANSFER_MESSAGE_HASH_RESPONSE,
  MOCK_TVL_DATA,
  MOCK_TVL_HISTORY,
  MOCK_PRICE_HISTORY,
  MOCK_TOKEN_INFO,
} from './api-mock.data';

const MOCK_DELAY = 200;

@Injectable()
export class MockApiService implements CustosApi {
  health(): Observable<{ status: string }> {
    return of({ status: 'ok' }).pipe(delay(50));
  }

  getMarkets(): Observable<ApiMarket[]> {
    return of(MOCK_API_MARKETS).pipe(delay(MOCK_DELAY));
  }

  getMarket(params: MarketQueryParams): Observable<ApiMarket> {
    const market = MOCK_API_MARKETS.find(
      (m) => m.chain_id === params.chain_id && m.market_id === params.market_id,
    );
    return of(market ?? ({} as ApiMarket)).pipe(delay(MOCK_DELAY));
  }

  getMarketPrice(params: MarketQueryParams): Observable<ApiMarketPrice> {
    const price = MOCK_API_MARKET_PRICES[params.market_id] ?? MOCK_API_MARKET_PRICE;
    return of(price).pipe(delay(MOCK_DELAY));
  }

  getCycles(
    params: MarketQueryParams & PaginatedQueryParams,
  ): Observable<ApiCycle[]> {
    const filtered = MOCK_API_CYCLES
      .filter(
        (c) => c.chain_id === params.chain_id && c.market_id === params.market_id,
      )
      .sort((a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime());
    const limited = filtered.slice(0, params.limit ?? 50);
    return of(limited).pipe(delay(MOCK_DELAY));
  }

  getCycle(params: CycleQueryParams): Observable<ApiCycle> {
    const cycle = MOCK_API_CYCLES.find(
      (c) =>
        c.chain_id === params.chain_id &&
        c.market_id === params.market_id &&
        c.cycle_id === params.cycle_id,
    );
    return of(cycle ?? ({} as ApiCycle)).pipe(delay(MOCK_DELAY));
  }

  getOrders(params: OrderQueryParams): Observable<ApiOrder[]> {
    let filtered = MOCK_API_ORDERS.filter(
      (o) => o.chain_id === params.chain_id && o.market_id === params.market_id,
    );
    if (params.user_address) {
      filtered = filtered.filter((o) => o.user_address.toLowerCase() === params.user_address!.toLowerCase());
    }
    return of(filtered.slice(0, params.limit ?? 50)).pipe(delay(MOCK_DELAY));
  }

  getOrdersByCycle(
    params: CycleQueryParams & PaginatedQueryParams & { user_address?: string },
  ): Observable<ApiOrder[]> {
    let filtered = MOCK_API_ORDERS.filter(
      (o) =>
        o.chain_id === params.chain_id &&
        o.market_id === params.market_id &&
        o.cycle_id === params.cycle_id,
    );
    if (params.user_address) {
      filtered = filtered.filter((o) => o.user_address.toLowerCase() === params.user_address!.toLowerCase());
    }
    return of(filtered.slice(0, params.limit ?? 50)).pipe(delay(MOCK_DELAY));
  }

  getOrderSettlements(params: OrderQueryParams): Observable<ApiOrderSettlement[]> {
    let filtered = MOCK_API_ORDER_SETTLEMENTS.filter(
      (s) => s.chain_id === params.chain_id && s.market_id === params.market_id,
    );
    if (params.user_address) {
      filtered = filtered.filter((s) => s.user_address.toLowerCase() === params.user_address!.toLowerCase());
    }
    return of(filtered.slice(0, params.limit ?? 50)).pipe(delay(MOCK_DELAY));
  }

  // Paged variants — mock always returns hasMore: false
  private toPagedResult<T>(items: T[]): PagedResult<T> {
    return { items, total: items.length, hasMore: false };
  }

  getOrdersPaged(params: OrderQueryParams): Observable<PagedResult<ApiOrder>> {
    let filtered = MOCK_API_ORDERS.filter(
      (o) => o.chain_id === params.chain_id && o.market_id === params.market_id,
    );
    if (params.user_address) {
      filtered = filtered.filter((o) => o.user_address.toLowerCase() === params.user_address!.toLowerCase());
    }
    const offset = params.offset ?? 0;
    const limit = params.limit ?? 50;
    const page = filtered.slice(offset, offset + limit);
    return of({ items: page, total: filtered.length, hasMore: offset + page.length < filtered.length }).pipe(delay(MOCK_DELAY));
  }

  getOrderSettlementsPaged(params: OrderQueryParams): Observable<PagedResult<ApiOrderSettlement>> {
    let filtered = MOCK_API_ORDER_SETTLEMENTS.filter(
      (s) => s.chain_id === params.chain_id && s.market_id === params.market_id,
    );
    if (params.user_address) {
      filtered = filtered.filter((s) => s.user_address.toLowerCase() === params.user_address!.toLowerCase());
    }
    const offset = params.offset ?? 0;
    const limit = params.limit ?? 50;
    const page = filtered.slice(offset, offset + limit);
    return of({ items: page, total: filtered.length, hasMore: offset + page.length < filtered.length }).pipe(delay(MOCK_DELAY));
  }

  getLockers(params: LockerListQueryParams): Observable<ApiLocker[]> {
    const filtered = [MOCK_API_LOCKER].filter((l) => l.chain_id === params.chain_id);
    return of(filtered).pipe(delay(MOCK_DELAY));
  }

  getLockerOrdersPaged(params: LockerOrderQueryParams): Observable<PagedResult<ApiLockerOrder>> {
    let filtered = MOCK_API_LOCKER_ORDERS.filter(
      (o) => o.chain_id === params.chain_id,
    );
    if (params.locker_id) {
      filtered = filtered.filter((o) => o.locker_id === params.locker_id);
    }
    if (params.user_address) {
      filtered = filtered.filter((o) => o.user_address.toLowerCase() === params.user_address!.toLowerCase());
    }
    const offset = params.offset ?? 0;
    const limit = params.limit ?? 50;
    const page = filtered.slice(offset, offset + limit);
    return of({ items: page, total: filtered.length, hasMore: offset + page.length < filtered.length }).pipe(delay(MOCK_DELAY));
  }

  getLocker(params: LockerQueryParams): Observable<ApiLocker> {
    if (
      MOCK_API_LOCKER.chain_id === params.chain_id &&
      MOCK_API_LOCKER.locker_id === params.locker_id
    ) {
      return of(MOCK_API_LOCKER).pipe(delay(MOCK_DELAY));
    }
    return of({} as ApiLocker).pipe(delay(MOCK_DELAY));
  }

  getLockerOrders(params: LockerOrderQueryParams): Observable<ApiLockerOrder[]> {
    let filtered = MOCK_API_LOCKER_ORDERS.filter(
      (o) => o.chain_id === params.chain_id,
    );
    if (params.locker_id) {
      filtered = filtered.filter((o) => o.locker_id === params.locker_id);
    }
    if (params.user_address) {
      filtered = filtered.filter((o) => o.user_address.toLowerCase() === params.user_address!.toLowerCase());
    }
    return of(filtered.slice(0, params.limit ?? 50)).pipe(delay(MOCK_DELAY));
  }

  getLockerUnlockOrders(
    _params: LockerOrderQueryParams,
  ): Observable<ApiLockerUnlockOrder[]> {
    // Mock: no unlock orders by default
    return of([] as ApiLockerUnlockOrder[]).pipe(delay(MOCK_DELAY));
  }

  createBetSignature(
    _body: BetSignatureRequest,
  ): Observable<BetSignatureResponse> {
    return of(MOCK_BET_SIGNATURE_RESPONSE).pipe(delay(MOCK_DELAY));
  }

  createTransferMessageHash(
    _body: TransferMessageHashRequest,
  ): Observable<TransferMessageHashResponse> {
    return of(MOCK_TRANSFER_MESSAGE_HASH_RESPONSE).pipe(delay(MOCK_DELAY));
  }

  getTvl(): Observable<ApiTvl> {
    return of(MOCK_TVL_DATA).pipe(delay(MOCK_DELAY));
  }

  getTvlHistory(range: string): Observable<ApiTvlHistory[]> {
    const data = MOCK_TVL_HISTORY[range] ?? MOCK_TVL_HISTORY['total'];
    return of(data).pipe(delay(MOCK_DELAY));
  }

  getPriceHistory(_marketId: string): Observable<ApiPriceHistory[]> {
    return of(MOCK_PRICE_HISTORY).pipe(delay(MOCK_DELAY));
  }

  getTokenInfo(_tokenSymbol: string): Observable<ApiTokenInfo> {
    return of(MOCK_TOKEN_INFO).pipe(delay(MOCK_DELAY));
  }

  getMarketPriceHistory(_params: { chain_id: number; market_id: string }): Observable<{ price: number; captured_at: string; timestamp?: string }[]> {
    return of(MOCK_PRICE_HISTORY.map((h) => ({ price: h.value, captured_at: h.date, timestamp: h.date }))).pipe(delay(MOCK_DELAY));
  }

  getLockerPrice(_params: { chain_id: number; locker_id: string }): Observable<{ price: number }> {
    return of({ price: 0 }).pipe(delay(MOCK_DELAY));
  }

  getTotalTvl(): Observable<{ time: string; usd_value: number }[]> {
    return of([]).pipe(delay(MOCK_DELAY));
  }

  getTrustLockerTvl(): Observable<{ time: string; usd_value: number }[]> {
    return of([]).pipe(delay(MOCK_DELAY));
  }

  getRollLockerTvl(): Observable<{ time: string; usd_value: number }[]> {
    return of([]).pipe(delay(MOCK_DELAY));
  }

  getStakingRewardsPending(_params: { user_address: string }): Observable<StakingRewardPending[]> {
    return of([]).pipe(delay(MOCK_DELAY));
  }

  getStakingRewardsHistory(_params: { user_address: string }): Observable<StakingRewardSnapshot[]> {
    return of([]).pipe(delay(MOCK_DELAY));
  }

  getLockerRecords(_params: { limit?: number; user_address?: string }): Observable<LockerRecord[]> {
    return of([]).pipe(delay(MOCK_DELAY));
  }

  getLockerClaims(_params: { limit?: number; locker_id?: string }): Observable<LockerClaim[]> {
    return of([]).pipe(delay(MOCK_DELAY));
  }

  getTables(): Observable<{ tables: string[] }> {
    return of({
      tables: [
        'cycles', 'indexer_checkpoints', 'locker', 'locker_checkpoints',
        'locker_orders', 'markets', 'order_settlements', 'orders',
      ],
    }).pipe(delay(MOCK_DELAY));
  }

  getTableSchema(
    _tableName: string,
  ): Observable<{ table: string; columns: TableSchemaColumn[] }> {
    return of({ table: _tableName, columns: [] }).pipe(delay(MOCK_DELAY));
  }

  getTableCount(
    _tableName: string,
  ): Observable<{ table: string; count: number }> {
    return of({ table: _tableName, count: 0 }).pipe(delay(MOCK_DELAY));
  }

  getTableRows(
    _tableName: string,
    _params?: TableRowsParams,
  ): Observable<TableRowsResponse> {
    return of({
      table: _tableName,
      limit: _params?.limit ?? 1000,
      offset: _params?.offset ?? 0,
      order_by: _params?.order_by ?? 'id',
      desc: _params?.desc ?? false,
      rows: [],
    }).pipe(delay(MOCK_DELAY));
  }
}
